import axios, { AxiosError, type AxiosInstance, type AxiosRequestConfig } from "axios";
import type { ProblemDocument } from "./types";
import { recordPilotError } from "../lib/pilotErrorLog";

// Centralised API client, ported from Summit Engineering. Every failure is normalised into an ApiError carrying the
// RFC 7807 problem document and the correlation id, so any error a person sees can be traced to a server log line.
// Who the caller is comes from the registered header provider: the Development persona header, or an Entra bearer token.

export const API_BASE_URL: string = import.meta.env.VITE_API_BASE_URL ?? "/api/v1";

/**
 * The RFC 7807 problem types the API uses for 428 Precondition Required. They are different refusals: a stale write is
 * a conflict the person resolves by reloading; a step-up is the "confirm it's you" authenticator step.
 */
export const PROBLEM_TYPES = {
  staleWrite: "https://summit-aviation.com/problems/stale-write",
  stepUpRequired: "https://summit-aviation.com/problems/step-up-required",
  /** O-R90: a signed act needs a one-time challenge for this act and record (Summit Engineering parity). */
  signatureRequired: "https://summit-aviation.com/problems/signature-required"
} as const;

/** The header a signed request carries its one-time challenge in (O-R90). */
export const SIGNATURE_HEADER = "X-SummitOps-Signature";

/** Raised on window when the API asks the signed-in person to confirm it is them. Only a step-up problem raises it. */
export const STEP_UP_EVENT = "summitops:step-up-required";

export class ApiError extends Error {
  readonly status: number;
  readonly title: string;
  readonly correlationId: string | null;
  /** The problem document's `type`, or null when the server sent none. */
  readonly type: string | null;
  /** RFC 7807 extension members (e.g. `signatureRequired`, `errors`). */
  readonly extensions: Record<string, unknown>;

  constructor(status: number, title: string, detail: string | undefined, correlationId: string | null, extensions: Record<string, unknown> = {}) {
    super(detail ?? title);
    this.name = "ApiError";
    this.status = status;
    this.title = title;
    this.correlationId = correlationId;
    this.type = typeof extensions.type === "string" ? extensions.type : null;
    this.extensions = extensions;
  }
}

/** What a 428 says to the person. Only the step-up type gets the "confirm it's you" wording. */
function preconditionTitle(type: string | undefined): string {
  if (type === PROBLEM_TYPES.stepUpRequired || type === PROBLEM_TYPES.signatureRequired)
    return "Not signed — you did not confirm it was you, so nothing was changed.";
  if (type === PROBLEM_TYPES.staleWrite) return "Someone changed this first. Reload to see the current value.";
  return "Nothing was changed. Reload and try again.";
}

type HeaderProvider = () => Promise<Record<string, string>> | Record<string, string>;
let headerProvider: HeaderProvider = () => ({});

/** The auth layer registers how to identify the caller (Development persona header, or later an Entra bearer token). */
export function setAuthHeaderProvider(provider: HeaderProvider): void {
  headerProvider = provider;
}

/** The caller's identity headers, for requests made outside axios. */
export async function getAuthHeaders(): Promise<Record<string, string>> {
  return await headerProvider();
}

/** Raised when the API refuses a request because nobody is signed in. */
export const UNAUTHORIZED_EVENT = "summitops:unauthorized";

/**
 * "Confirm it's you" (the authenticator step-up, Q-O12; ported from Summit Engineering's MFA prompt): when the API answers
 * 428 with the step-up problem type, the registered provider asks for the code from the person's authenticator app. It
 * resolves true once the API has verified this sign-in server-side, and the refused request is then sent again, once.
 */
type StepUpProvider = (problem?: StepUpProblem) => Promise<boolean>;
let stepUpProvider: StepUpProvider | null = null;

/**
 * The step-up 428 as the prompt needs it. `signingIdle` (O-R90): the sign-in is verified, but nothing was signed for an
 * hour, so signing needs a fresh code — the prompt must ask for one even though the sign-in shows as verified.
 */
export type StepUpProblem = ProblemDocument & { signingIdle?: boolean };

/** Registered by <StepUpProvider /> at the root of the app. */
export function setStepUpProvider(provider: StepUpProvider | null): void {
  stepUpProvider = provider;
}

/** Request options understood by this client: `skipStepUpPrompt` = never open the code prompt for this request. */
export type StepUpRequestConfig = AxiosRequestConfig & { skipStepUpPrompt?: boolean };

/** True when an error is the API's "confirm it's you" answer (428, step-up problem type). */
export function isStepUpRequired(error: unknown): boolean {
  return error instanceof ApiError && error.status === 428 && error.type === PROBLEM_TYPES.stepUpRequired;
}

/** The signature-required 428 (O-R90): which signed act and which record the challenge must be issued for. */
type SignatureProblem = ProblemDocument & { act?: string; subject?: string; signatureRequired?: boolean };

/** A one-time signing challenge (O-R90): sent once, in {@link SIGNATURE_HEADER}, before it expires. */
export interface SigningChallenge {
  challengeId: string;
  expiresAtUtc: string;
  act: string;
  subject: string;
  /** When signing stops without a fresh authenticator code (null when the role does not use the authenticator step). */
  signingEndsAtUtc: string | null;
}

export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30_000,
  headers: { Accept: "application/json" }
});

apiClient.interceptors.request.use(async (config) => {
  const headers = await headerProvider();
  // A header the caller set explicitly wins.
  for (const [name, value] of Object.entries(headers)) if (!config.headers.has(name)) config.headers.set(name, value);
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ProblemDocument>) => {
    const status = error.response?.status ?? 0;
    const problem = error.response?.data;
    const correlationId = problem?.correlationId ?? (error.response?.headers?.["x-correlation-id"] as string | undefined) ?? null;

    if (status === 401) window.dispatchEvent(new CustomEvent(UNAUTHORIZED_EVENT));
    if (status === 428 && problem?.type === PROBLEM_TYPES.stepUpRequired) {
      window.dispatchEvent(new CustomEvent(STEP_UP_EVENT, { detail: problem }));
      // Ask for the authenticator code once, then resend. Not a sign-out: the Microsoft sign-in itself is fine.
      const config = error.config as (typeof error.config & { _stepUp?: boolean; skipStepUpPrompt?: boolean }) | undefined;
      if (config && !config._stepUp && !config.skipStepUpPrompt && stepUpProvider) {
        config._stepUp = true;
        if (await stepUpProvider(problem as StepUpProblem)) return apiClient.request(config);
      }
    }

    // O-R90 (Summit Engineering parity): a signed act needs a one-time challenge for THIS act and THIS record. The person
    // already confirmed in the signing dialog; ask the API for the challenge (which asks for the authenticator code first
    // when the sign-in needs it, or when nothing was signed for an hour) and send the request again, once, carrying it.
    if (status === 428 && problem?.type === PROBLEM_TYPES.signatureRequired) {
      const signature = problem as SignatureProblem;
      const config = error.config as (typeof error.config & { _signature?: boolean }) | undefined;
      if (config && !config._signature && signature.act && signature.subject) {
        config._signature = true;
        let challenge: SigningChallenge;
        try {
          challenge = (await apiClient.post<SigningChallenge>("/signatures/challenge", { act: signature.act, subject: signature.subject })).data;
        } catch (challengeError) {
          // Cancelled or refused: the original request signed nothing. Report it in the same plain words.
          return Promise.reject(
            challengeError instanceof ApiError
              ? challengeError
              : new ApiError(428, preconditionTitle(PROBLEM_TYPES.signatureRequired), problem?.detail, correlationId, signature as unknown as Record<string, unknown>)
          );
        }

        config.headers.set(SIGNATURE_HEADER, challenge.challengeId);
        return apiClient.request(config);
      }
    }

    const title =
      status === 428
        ? preconditionTitle(problem?.type)
        : status === 0
          ? "The Summit Operations API could not be reached."
          : status === 403
            ? "Your role does not include access to this."
            : status === 429
              ? "Too many requests — wait a moment and try again."
              : (problem?.title ?? `Request failed (${status}).`);

    // Pilot feedback (O-R34): remember the failure in this tab so "Pilot feedback" can offer its reference. Not 401/428.
    if (status !== 401 && status !== 428) recordPilotError({ status, title, correlationId, path: error.config?.url ?? null });

    return Promise.reject(new ApiError(status, title, problem?.detail, correlationId, (problem ?? {}) as unknown as Record<string, unknown>));
  }
);
